import {
  ArrowLeft,
  BookOpen,
  Brain,
  Check,
  CloudDownload,
  LoaderCircle,
  Play,
  Sparkles,
  Target
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { postJson, putJson, request } from "../api";
import { useAuth } from "../auth";
import { ErrorCard, Loading } from "../components/AsyncState";
import { isBookOffline, loadBookFile } from "../offline";
import type { AnalysisJob, Book, PreviewItem, ReadingGoal, Section } from "../types";
import { analysisLabel, errorMessage, formatNumber, vocabStatusLabel } from "../utils";

export function BookPage() {
  const { bookId = "" } = useParams();
  const { user } = useAuth();
  const [book, setBook] = useState<Book | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [job, setJob] = useState<AnalysisJob | null>(null);
  const [preview, setPreview] = useState<PreviewItem[]>([]);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [offline, setOffline] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [loadedBook, loadedJob] = await Promise.all([
        request<Book>(`/api/books/${bookId}`),
        request<AnalysisJob | null>(`/api/books/${bookId}/analysis`)
      ]);
      setBook(loadedBook);
      setJob(loadedJob);
      if (loadedBook.analysis_status === "completed") {
        const loadedSections = await request<Section[]>(`/api/books/${bookId}/sections`);
        setSections(loadedSections);
        if (loadedSections.length) {
          const firstIndex = loadedSections[0].spine_index;
          setStart((value) =>
            loadedSections.some((section) => section.spine_index === value) ? value : firstIndex
          );
          setEnd((value) =>
            loadedSections.some((section) => section.spine_index === value) ? value : firstIndex
          );
        }
      }
      if (user) setOffline(await isBookOffline(user.id, bookId));
      setError("");
    } catch (reason) {
      setError(errorMessage(reason));
    }
  }, [bookId, user]);

  useEffect(() => void load(), [load]);
  useEffect(() => {
    if (!job || !["queued", "running"].includes(job.status)) return;
    const timer = window.setInterval(() => void load(), 1800);
    return () => window.clearInterval(timer);
  }, [job, load]);

  async function analyze() {
    setBusy("analysis");
    try {
      setJob(await postJson<AnalysisJob>(`/api/books/${bookId}/analysis`, {}));
      await load();
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy("");
    }
  }

  async function download() {
    if (!user) return;
    setBusy("offline");
    try {
      await loadBookFile(user.id, bookId, book ?? undefined);
      setOffline(true);
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy("");
    }
  }

  async function generatePreview() {
    setBusy("preview");
    try {
      setPreview(
        await request<PreviewItem[]>(
          `/api/vocabulary/preview/${bookId}?start_spine_index=${start}&end_spine_index=${end}&limit=60`
        )
      );
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy("");
    }
  }

  async function setKnown(item: PreviewItem, status: "known_receptive" | "unknown") {
    await putJson(`/api/vocabulary/${item.lexeme.id}`, { status, source_type: "preview" });
    setPreview((items) =>
      items.map((candidate) =>
        candidate.lexeme.id === item.lexeme.id
          ? {
              ...candidate,
              status,
              known_probability: status === "known_receptive" ? 0.9 : 0.08
            }
          : candidate
      )
    );
  }

  async function createGoal() {
    setBusy("goal");
    try {
      await postJson<ReadingGoal>(`/api/reading/books/${bookId}/goals`, {
        title: `阅读第 ${start + 1}–${end + 1} 节`,
        start_spine_index: start,
        end_spine_index: end,
        target_words: Math.max(
          100,
          sections
            .filter((section) => section.spine_index >= start && section.spine_index <= end)
            .reduce((sum, section) => sum + section.lexical_token_count, 0)
        )
      });
      setBusy("goal-done");
      window.setTimeout(() => setBusy(""), 1400);
    } catch (reason) {
      setError(errorMessage(reason));
      setBusy("");
    }
  }

  if (error && !book) return <ErrorCard message={error} retry={() => void load()} />;
  if (!book) return <Loading label="正在打开书籍" />;

  const analyzing = ["queued", "running"].includes(book.analysis_status);
  return (
    <div className="stack">
      <Link className="back-link" to="/library"><ArrowLeft size={16} /> 返回书库</Link>
      <header className="book-hero">
        <div className="book-cover book-cover--large"><span>{book.language.toUpperCase()}</span><strong>{book.title[0]}</strong><small>flussik edition</small></div>
        <div className="book-hero__copy">
          <span className={`status-pill status-pill--${book.analysis_status}`}>
            <Sparkles size={13} /> {analysisLabel[book.analysis_status]}
          </span>
          <h1>{book.title}</h1>
          <p>{book.author || "作者未知"} · {formatNumber(book.token_count)} 词</p>
          <div className="button-row">
            <Link className="primary-button" to={`/read/${book.id}`}><BookOpen size={17} /> 开始阅读</Link>
            <button className="secondary-button" onClick={() => void download()} disabled={offline || busy === "offline"}>
              {busy === "offline" ? <LoaderCircle className="spin" size={16} /> : offline ? <Check size={16} /> : <CloudDownload size={16} />}
              {offline ? "已可离线阅读" : "下载到此设备"}
            </button>
          </div>
        </div>
      </header>
      {error && <ErrorCard message={error} />}

      <section className="panel analysis-panel">
        <div className="panel__heading">
          <div><span className="eyebrow"><Brain size={14} /> Analyse locale</span><h2>全书法语分析</h2></div>
          {book.analysis_status !== "completed" && !analyzing && (
            <button className="primary-button" onClick={() => void analyze()} disabled={busy === "analysis"}>
              <Play size={16} /> 开始分析
            </button>
          )}
        </div>
        {analyzing && (
          <div className="analysis-progress">
            <div><span style={{ width: `${Math.max(3, (job?.progress ?? 0) * 100)}%` }} /></div>
            <p>
              {book.analysis_status === "queued"
                ? "任务已排队，低资源模式一次只分析一本书。"
                : `正在处理第 ${job?.current_section ?? 0} / ${job?.total_sections || "?"} 节`}
            </p>
          </div>
        )}
        {book.analysis_status === "not_started" && <p className="muted">阅读不必等待。分析会在服务器上做词形还原、词性与词频统计，不调用 LLM。</p>}
        {book.analysis_status === "failed" && <p className="form-error">{job?.error || "分析失败，可以重新开始。"}</p>}
        {book.analysis_status === "completed" && (
          <div className="analysis-stats">
            <span><strong>{book.section_count}</strong> 可读章节</span>
            <span><strong>{formatNumber(book.lexical_token_count)}</strong> 法语词元</span>
            <span><strong>{formatNumber(book.unique_lexeme_count)}</strong> lemma + 词性</span>
          </div>
        )}
      </section>

      {book.analysis_status === "completed" && (
        <section className="panel">
          <div className="panel__heading">
            <div><span className="eyebrow"><Target size={14} /> Prochaine lecture</span><h2>规划并预习下一段</h2></div>
          </div>
          <div className="range-form">
            <label>从<select value={start} onChange={(event) => { const value = Number(event.target.value); setStart(value); setEnd((old) => Math.max(old, value)); }}>
              {sections.map((section) => <option key={section.id} value={section.spine_index}>{section.spine_index + 1}. {section.title || "未命名章节"}</option>)}
            </select></label>
            <label>到<select value={end} onChange={(event) => setEnd(Number(event.target.value))}>
              {sections.filter((section) => section.spine_index >= start).map((section) => <option key={section.id} value={section.spine_index}>{section.spine_index + 1}. {section.title || "未命名章节"}</option>)}
            </select></label>
            <button className="secondary-button" onClick={() => void createGoal()}>
              {busy === "goal-done" ? <Check size={16} /> : <Target size={16} />} {busy === "goal-done" ? "目标已保存" : "设为阅读目标"}
            </button>
            <button className="primary-button" onClick={() => void generatePreview()} disabled={busy === "preview"}>
              {busy === "preview" ? <LoaderCircle className="spin" size={16} /> : <Sparkles size={16} />} 生成预习表
            </button>
          </div>
          {preview.length > 0 && (
            <div className="preview-list">
              <div className="preview-list__intro">
                <strong>建议先看这 {preview.length} 个词</strong>
                <span>按“可能不认识 × 本段出现次数 × 稀有度”排序；不是 CEFR 判定。</span>
              </div>
              {preview.map((item) => (
                <article key={item.lexeme.id}>
                  <div>
                    <strong>{item.lexeme.lemma}</strong>
                    <span>{item.lexeme.upos.toLowerCase()} · Zipf {item.lexeme.zipf_frequency?.toFixed(1) ?? "—"}</span>
                  </div>
                  <div className="preview-list__frequency">
                    <strong>{item.token_count}×</strong><span>{vocabStatusLabel[item.status] || item.status}</span>
                  </div>
                  <div className="preview-list__actions">
                    <button onClick={() => void setKnown(item, "known_receptive")} className={item.status === "known_receptive" ? "selected" : ""}>认识</button>
                    <button onClick={() => void setKnown(item, "unknown")} className={item.status === "unknown" ? "selected danger" : ""}>不认识</button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
